import React, { useState, useEffect } from 'react';
import '../styles/AiAssistPanel.css';

const DEFAULT_SECTIONS = [
  { key: 'refined_symptoms', title: 'Refined Symptoms', icon: '🔍' },
  { key: 'suggested_diagnosis', title: 'Suggested Diagnosis', icon: '🩺' },
  { key: 'suggested_treatment', title: 'Treatment Plan', icon: '💊' },
];

/**
 * AiAssistPanel — A reusable slide-over panel that displays AI suggestions
 * or read-only AI reports, depending on the mode.
 *
 * Props:
 *  - visible (bool) — controls panel visibility
 *  - onClose (fn) — called when panel is closed
 *  - onApply (fn) — called with { fieldName: value } for selected suggestions (suggestions mode only)
 *  - onApplyAll (fn) — called with all selected suggestions mapped to form fields (suggestions mode only)
 *  - suggestions (object) — the AI response data
 *  - loading (bool)
 *  - error (string)
 *  - cached (bool) — shows "cached result" badge
 *  - fieldMapping (object) — maps AI response keys to form field names (suggestions mode only)
 *  - onRetry (fn) — retry the AI call
 *  - mode ('suggestions'|'report') — panel behavior: apply-able suggestions vs read-only report
 *  - sections (array) — configurable sections: [{ key, title, icon }]
 *  - panelTitle (string) — optional panel header title
 *  - reasoningKey (string) — which response key holds reasoning/summary (default: 'reasoning')
 *  - confidenceKey (string) — which response key holds confidence/severity (default: 'confidence')
 */
const AiAssistPanel = ({
  visible,
  onClose,
  onApply,
  onApplyAll,
  suggestions,
  loading,
  error,
  cached,
  fieldMapping,
  onRetry,
  mode = 'suggestions',
  sections: sectionsProp,
  panelTitle,
  reasoningKey = 'reasoning',
  confidenceKey = 'confidence',
}) => {
  const isReport = mode === 'report';
  const sections = sectionsProp || DEFAULT_SECTIONS;
  const headerTitle = panelTitle || (isReport ? 'Analysis Report' : 'Suggestions');

  // All sections selected by default (only used in suggestions mode)
  const [selected, setSelected] = useState(() =>
    sections.reduce((acc, s) => ({ ...acc, [s.key]: true }), {})
  );

  // Reset selections when new suggestions arrive
  useEffect(() => {
    if (suggestions) {
      setSelected(sections.reduce((acc, s) => ({ ...acc, [s.key]: true }), {}));
    }
  }, [suggestions]);

  if (!visible) return null;

  const toggleSelection = (key) => {
    if (isReport) return;
    setSelected(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleApplySelected = () => {
    if (isReport || !suggestions || !fieldMapping) return;
    const mapped = {};
    Object.entries(fieldMapping).forEach(([aiKey, formField]) => {
      if (selected[aiKey] && suggestions[aiKey]) {
        mapped[formField] = suggestions[aiKey];
      }
    });
    if (Object.keys(mapped).length > 0) {
      onApplyAll(mapped);
    }
  };

  const selectedCount = sections.filter(s => selected[s.key] && suggestions?.[s.key]).length;
  const confidenceValue = suggestions?.[confidenceKey];
  const reasoningValue = suggestions?.[reasoningKey];

  return (
    <div className="ai-panel-backdrop" onClick={onClose}>
      <div className="ai-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="ai-panel-header">
          <div className="ai-panel-title">
            <span className="ai-panel-icon">{isReport ? '📋' : '✨'}</span>
            <h3>{headerTitle}</h3>
            {cached && <span className="ai-badge ai-badge-cached">Cached</span>}
          </div>
          <button className="ai-panel-close" onClick={onClose} title="Close">
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="ai-panel-body">
          {loading && (
            <div className="ai-panel-loading">
              <div className="ai-spinner"></div>
              <p>Analyzing patient data...</p>
              <p className="ai-loading-sub">This may take a few seconds</p>
            </div>
          )}

          {error && (
            <div className="ai-panel-error">
              <span className="ai-error-icon">⚠️</span>
              <p>{error}</p>
              {onRetry && (
                <button className="ai-btn ai-btn-retry" onClick={onRetry}>
                  Try Again
                </button>
              )}
            </div>
          )}

          {!loading && !error && suggestions && (
            <>
              {/* Confidence/Severity Badge */}
              {confidenceValue && (
                <div className="ai-confidence-banner">
                  <span className={`ai-badge ai-badge-confidence ai-confidence-${confidenceValue}`}>
                    {confidenceValue} {isReport ? 'severity' : 'confidence'}
                  </span>
                </div>
              )}

              {sections.map((section) => (
                <div
                  key={section.key}
                  className={`ai-suggestion-section ${isReport ? 'ai-report-section' : (selected[section.key] ? 'ai-selected' : 'ai-unselected')}`}
                  onClick={() => !isReport && suggestions[section.key] && toggleSelection(section.key)}
                  style={{ cursor: isReport ? 'default' : (suggestions[section.key] ? 'pointer' : 'default') }}
                >
                  <div className="ai-suggestion-header">
                    {!isReport && suggestions[section.key] && (
                      <input
                        type="checkbox"
                        className="ai-checkbox"
                        checked={selected[section.key]}
                        onChange={() => toggleSelection(section.key)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                    <span className="ai-section-icon">{section.icon}</span>
                    <h4>{section.title}</h4>
                  </div>
                  <div className="ai-suggestion-content">
                    <p>{suggestions[section.key] || (isReport ? 'No issues found' : 'No suggestion available')}</p>
                  </div>
                </div>
              ))}

              {/* Reasoning / Summary */}
              {reasoningValue && (
                <div className="ai-reasoning">
                  <h4>{isReport ? 'Summary' : 'Clinical Reasoning'}</h4>
                  <p>{reasoningValue}</p>
                </div>
              )}
            </>
          )}
        </div>

        {!loading && !error && suggestions && !isReport && (
          <div className="ai-panel-footer">
            <button
              className="ai-btn ai-btn-apply-all"
              onClick={handleApplySelected}
              disabled={selectedCount === 0}
            >
              Apply {selectedCount === sections.length ? 'All' : selectedCount} Suggestion{selectedCount !== 1 ? 's' : ''}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AiAssistPanel;
